/* src/render/tileMesh.ts — wrap worker output in Three.js objects.
 *
 * The buffers arriving here were built in a worker and transferred, not copied. This file
 * hands them straight to BufferAttribute with no parsing, no per-vertex loop, and no
 * allocation beyond the Three.js wrappers themselves — the whole point of the packed-array
 * contract in src/workers/protocol.ts.
 */

import { BufferAttribute, BufferGeometry, Group, Mesh, type Object3D } from 'three';
import type { PackedMesh } from '../workers/protocol.ts';
import { castsShadow, materialFor, type LayerMaterials } from './materials.ts';

export interface TileObject {
  readonly group: Object3D;
  readonly geometries: BufferGeometry[];
  /** Mutable: incrementally-committed tiles (tileManager) tally this as meshes land. */
  triangles: number;
  dispose: () => void;
}

/*
 * Three.js keeps every attribute's TypedArray alive after uploading it to the GPU, which
 * doubles the memory cost of a world made of streamed geometry. Anything the CPU never
 * touches again frees its JS copy the moment WebGL has it. What the CPU *does* touch:
 * click raycasts, which read `position`, the index, and `userData.featureIds` — and only on
 * the pickable ring's buildings and roofs. Everything else is render-only.
 */
function freeOnUpload(attr: BufferAttribute): BufferAttribute {
  attr.onUpload(function (this: BufferAttribute) {
    // Deliberate: three's types call this a TypedArray, but after upload the reference only
    // costs memory. Bounding spheres are computed before this fires; raycasts never see it.
    (this as unknown as { array: null }).array = null;
  });
  return attr;
}

/** Flat layers draw without depth (materials.ts); this stages them behind everything solid.
 * Landuse below water, so harbours and lakes always paint over their shorelines. */
const FLAT_ORDER: Readonly<Record<string, number>> = { landuse: -40, water: -30 };

/** One packed layer → one renderable mesh. The unit of incremental tile commits.
 * `solidOrder` stages the depth-tested layers: the far ring's buildings duplicate the near
 * ring's underneath it, and left to the default depth sort three often drew the huge far
 * meshes FIRST — millions of fully-shaded fragments painted over again by the street ring
 * every frame. Drawing far solids after near lets early-z reject the hidden copy instead. */
export function createLayerMesh(
  packed: PackedMesh,
  materials: LayerMaterials,
  pickable: boolean,
  orderBias: number,
  solidOrder = 0,
  shadows = true,
): { mesh: Mesh; geometry: BufferGeometry; triangles: number } | null {
  if (packed.indices.length === 0) return null;

  const keepCpu = pickable && (packed.layer === 'buildings' || packed.layer === 'roofs');

  const geometry = new BufferGeometry();
  const position = new BufferAttribute(packed.positions, 3);
  if (!keepCpu) freeOnUpload(position);
  geometry.setAttribute('position', position);
  // Normals and colours arrive quantised to 8 bits; `normalized: true` restores 0…1 / −1…1
  // in the shader for free.
  geometry.setAttribute('normal', freeOnUpload(new BufferAttribute(packed.normals, 3, true)));
  geometry.setAttribute('color', freeOnUpload(new BufferAttribute(packed.colors, 3, true)));
  geometry.setAttribute('uv', freeOnUpload(new BufferAttribute(packed.uvs, 2)));
  // Walls carry a per-vertex facade-style index; the building material's shader reads it to
  // pick a layer from the facade texture array. Absent on every other layer.
  if (packed.texLayer) {
    geometry.setAttribute('aTexLayer', freeOnUpload(new BufferAttribute(packed.texLayer, 1)));
  }
  /*
   * Per-vertex source feature id, kept in `userData` for the CPU (click raycasts read it) —
   * and, for the two building layers only, ALSO as a float attribute for the GPU: the
   * interior's window view renders the city with the building you are standing in discarded
   * by feature id (materials.ts), which needs the id in the shader. Float32 cannot hold a
   * 9-digit OSM way id exactly, so both sides compare modulo 2^24 — a collision would hide
   * one extra building in sixteen million, which nobody will ever stand in front of.
   * Non-pickable rings (the far ring, the country context) skip both: their ids identify
   * nothing and the interior only ever hides a near-ring building.
   */
  if (keepCpu) {
    geometry.userData['featureIds'] = packed.featureIds;
    const feat = new Float32Array(packed.featureIds.length);
    for (let i = 0; i < packed.featureIds.length; i += 1) {
      feat[i] = (packed.featureIds[i] ?? 0) % 16777216;
    }
    geometry.setAttribute('aFeature', freeOnUpload(new BufferAttribute(feat, 1)));
  }
  const index = new BufferAttribute(packed.indices, 1);
  if (!keepCpu) freeOnUpload(index);
  geometry.setIndex(index);
  // Needed for frustum culling; cheap to compute once, and without it Three.js falls back
  // to never culling this mesh. Must run before the upload callbacks drop the arrays.
  geometry.computeBoundingSphere();

  const mesh = new Mesh(geometry, materialFor(materials, packed.layer));
  mesh.name = packed.layer;
  const flatOrder = FLAT_ORDER[packed.layer];
  mesh.renderOrder = flatOrder !== undefined ? flatOrder + orderBias : solidOrder;
  /*
   * Only buildings cast; the ground layers just receive. Letting flat ground quads cast
   * would double the shadow pass for shadows that land exactly on themselves.
   *
   * `shadows: false` (the far ring) opts a whole ring out of the shadow system. The sun's
   * frustum spans 520 m around the camera target — always inside the street ring — so a
   * far-ring mesh can neither throw a visible shadow nor stand in one. But its ~7 km tile
   * bounding sphere ALWAYS intersects the shadow frustum, so as a caster it was re-drawn
   * into the 2048² map on every shadow update (a permanent tax on every moving frame), and
   * as a receiver every one of its screen pixels paid the 9-tap PCF lookup for a map that
   * cannot cover it.
   */
  mesh.castShadow = shadows && castsShadow(packed.layer);
  mesh.receiveShadow = shadows;
  // Tiles are added and removed constantly; skipping the automatic matrix update and
  // flagging it manually keeps that off the per-frame path.
  mesh.matrixAutoUpdate = false;
  mesh.updateMatrix();

  return { mesh, geometry, triangles: packed.indices.length / 3 };
}

export function createTileObject(
  meshes: PackedMesh[],
  materials: LayerMaterials,
  pickable = true,
  orderBias = 0,
): TileObject {
  const group = new Group();
  const geometries: BufferGeometry[] = [];
  let triangles = 0;

  for (const packed of meshes) {
    const built = createLayerMesh(packed, materials, pickable, orderBias);
    if (!built) continue;
    group.add(built.mesh);
    geometries.push(built.geometry);
    triangles += built.triangles;
  }

  group.matrixAutoUpdate = false;

  return {
    group,
    geometries,
    triangles,
    dispose(): void {
      for (const g of geometries) g.dispose();
      group.clear();
    },
  };
}

/**
 * Place a tile after load or a floating-origin rebase. Geometry is never rebuilt: `scaleXZ`
 * is the ratio between the origin scale the tile was built with and the current one, applied
 * on the horizontal axes only (heights are true metres and must stay put). Near the origin
 * the ratio is ~1.0000; it is what keeps a tile built in Sydney meeting one built after a
 * flight to Perth without a seam. `y` lets the far LOD ring sit fractionally below the
 * street-level ring so transient overlap resolves without z-fighting.
 */
export function setTilePosition(
  tile: TileObject,
  x: number,
  z: number,
  y = 0,
  scaleXZ = 1,
): void {
  tile.group.position.set(x, y, z);
  tile.group.scale.set(scaleXZ, 1, scaleXZ);
  tile.group.updateMatrix();
}
